import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { queryKeys } from "@/lib/queryKeys";

export interface OrgResponse {
  id: string;
  name: string;
  slug: string;
  jira_site_url: string | null;
  gcp_project_id: string | null;
  allowed_email_domains: string[];
  created_at: string;
}

export function useOrganization() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const orgId = user?.org_id ?? null;

  const query = useQuery({
    queryKey: queryKeys.organizations.current(orgId ?? ""),
    queryFn: () => api<OrgResponse>(`/api/v1/organizations/${orgId}`),
    enabled: orgId !== null,
  });

  const patchMutation = useMutation({
    mutationFn: (fields: Partial<OrgResponse>) =>
      api<OrgResponse>(`/api/v1/organizations/${orgId}`, {
        method: "PATCH",
        body: JSON.stringify(fields),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.current(orgId ?? ""),
      });
    },
  });

  const patchOrg = useCallback(
    async (fields: Partial<OrgResponse>) => {
      if (!orgId) return;
      await patchMutation.mutateAsync(fields);
    },
    [orgId, patchMutation],
  );

  return {
    org: query.data ?? null,
    loading: query.isLoading,
    error: query.error ? "Failed to load organization" : null,
    patchOrg,
  };
}
